import React, { useState } from 'react';
import {
  Pen,
  Highlighter,
  Square,
  Circle,
  ArrowUpRight,
  Trash2,
  PenLine,
  X,
  Send,
  CheckCircle2,
  FileBadge,
  UserCheck,
  XCircle,
  ZoomIn,
  ZoomOut,
  Search,
} from 'lucide-react';
import {
  CONTRACT_TOOLS,
  COLOR_PALETTE,
  MIN_THICKNESS,
  MAX_THICKNESS,
} from './contractAnnotationTools';

const TOOL_ICONS = {
  pen: Pen,
  highlighter: Highlighter,
  square: Square,
  circle: Circle,
  arrow: ArrowUpRight,
};

export default function ContractAnnotationToolbar({
  isHost,
  activeToolId,
  onSelectTool,
  activeColorId,
  onSelectColor,
  thickness,
  onThicknessChange,
  fadeSeconds,
  onFadeSecondsChange,
  onClearAll,
  onSign,
  onSubmitSigned,
  onDecline,
  onClose,
  onPresent,
  presented,
  onStopPresenting,
  onDownloadCertificate,
  onMinimize,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  currentZoom,
  onSearch,
  connected,
  signedNotices,
  showSignedList,
  onToggleSignedList,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <div className="flex flex-col">
    {/* ─────────── BAR 1: Status (left) + Primary actions (right) ─────────── */}
    <div className="flex items-center gap-2 px-3 py-2 bg-[#001233] border-b border-white/10 text-white flex-nowrap whitespace-nowrap">
      {/* Live indicator (status on the left) */}
      <span
        className={`text-[10px] font-semibold uppercase px-2 py-1 rounded ${
          connected ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
        }`}
      >
        {connected ? '● Live' : '○ Offline'}
      </span>

      <div className="flex-1" />

      {/* Present to Participants — host only */}
      {isHost && onPresent && (
        <button
          type="button"
          onClick={onPresent}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold ${
            presented
              ? 'bg-[#21262D] text-gray-300 hover:bg-[#30363D]'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}
          title={presented ? 'Re-send to participants' : 'Present to participants'}
        >
          <Send size={14} />
          <span>{presented ? 'Re-send' : 'Present to Participants'}</span>
        </button>
      )}

      {/* Stop Presenting — host only, visible after presenting */}
      {isHost && presented && onStopPresenting && (
        <button
          type="button"
          onClick={onStopPresenting}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-600/80 hover:bg-red-600 text-white text-xs font-semibold"
          title="Stop presenting and remove contract from participants"
        >
          <XCircle size={14} />
          <span>Stop Presenting</span>
        </button>
      )}

      {/* Decline — visible to participant */}
      {onDecline && (
        <button
          type="button"
          onClick={onDecline}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#21262D] hover:bg-red-600 text-xs font-semibold text-gray-300 hover:text-white"
          title="Decline to sign"
        >
          <XCircle size={14} />
          <span>Decline</span>
        </button>
      )}

      {/* Submit signed contract — visible to participant */}
      {onSubmitSigned && (
        <button
          type="button"
          onClick={onSubmitSigned}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold"
          title="Submit signed contract"
        >
          <CheckCircle2 size={14} />
          <span>Submit</span>
        </button>
      )}

      {/* Search */}
      {onSearch && (
        <div className="flex items-center gap-1">
          {searchOpen ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  onSearch(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setSearchOpen(false);
                    setSearchQuery('');
                    onSearch('');
                  } else if (e.key === 'Enter') {
                    onSearch(searchQuery);
                  }
                }}
                placeholder="Search…"
                className="px-2 py-1 w-32 rounded-md bg-[#21262D] border border-[#30363D] text-xs text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500 flex-shrink-0"
              />
              <button
                type="button"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchQuery('');
                  onSearch('');
                }}
                className="p-1 text-gray-400 hover:text-white"
                title="Close search"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="p-1.5 rounded-md bg-[#21262D] hover:bg-[#30363D]"
              title="Search in document"
            >
              <Search size={14} />
            </button>
          )}
        </div>
      )}

      {onMinimize && (
        <button
          type="button"
          onClick={onMinimize}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#21262D] hover:bg-[#30363D] text-xs font-semibold"
          title="Minimize contract — return to meeting view"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14" />
          </svg>
          <span className="hidden md:inline">Minimize</span>
        </button>
      )}

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-[#21262D] hover:bg-[#30363D] text-xs"
          title="Close contract"
        >
          <X size={14} />
        </button>
      )}
    </div>

    {/* ─────────── BAR 2: Drawing tools + colors + size + fade + clear + zoom ─────────── */}
    <div className="flex items-center gap-2 px-3 py-2 bg-[#060621] border-b border-white/10 text-white flex-nowrap overflow-x-auto whitespace-nowrap">
      {isHost && (
        <>
          {/* Tools */}
          <div className="flex items-center gap-1">
            {CONTRACT_TOOLS.map((tool) => {
              const Icon = TOOL_ICONS[tool.id];
              const active = activeToolId === tool.id;
              return (
                <button
                  key={tool.id}
                  type="button"
                  onClick={() => onSelectTool(tool.id)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition ${
                    active
                      ? 'bg-purple-600 text-white shadow'
                      : 'bg-[#21262D] text-gray-200 hover:bg-[#30363D]'
                  }`}
                  title={tool.label}
                >
                  {Icon && <Icon size={14} />}
                  <span className="hidden sm:inline">{tool.label}</span>
                </button>
              );
            })}
          </div>

          <div className="w-px h-7 bg-[#30363D]" />

          {/* Color picker */}
          <div className="flex items-center gap-1">
            {COLOR_PALETTE.map((c) => {
              const active = activeColorId === c.id;
              const rgb = `rgb(${c.rgb.R}, ${c.rgb.G}, ${c.rgb.B})`;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onSelectColor(c.id)}
                  className={`w-6 h-6 rounded-full border-2 transition ${
                    active ? 'border-white scale-110' : 'border-[#30363D] hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: rgb }}
                  title={c.label}
                />
              );
            })}
          </div>

          <div className="w-px h-7 bg-[#30363D]" />

          {/* Thickness */}
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-gray-400">Size</label>
            <input
              type="range"
              min={MIN_THICKNESS}
              max={MAX_THICKNESS}
              value={thickness}
              onChange={(e) => onThicknessChange(Number(e.target.value))}
              className="w-24"
            />
            <span className="text-[11px] w-6 text-right">{thickness}</span>
          </div>

          {/* Fade duration */}
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-gray-400">Fade</label>
            <input
              type="number"
              min={1}
              max={120}
              value={fadeSeconds}
              onChange={(e) => onFadeSecondsChange(Number(e.target.value))}
              className="w-14 bg-[#21262D] border border-[#30363D] rounded px-2 py-1 text-xs"
            />
            <span className="text-[11px] text-gray-400">s</span>
          </div>

          {/* Clear all */}
          <button
            type="button"
            onClick={onClearAll}
            className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-[#21262D] hover:bg-red-600 text-xs"
            title="Clear all annotations"
          >
            <Trash2 size={13} />
            <span className="hidden sm:inline">Clear</span>
          </button>
        </>
      )}

      {/* Zoom controls (kept on Bar 2 with the other doc tools) */}
      {onZoomOut && (
        <div className="flex items-center gap-1 border-l border-[#30363D] pl-3 ml-auto">
          <button
            type="button"
            onClick={onZoomOut}
            className="p-1.5 rounded-md bg-[#21262D] hover:bg-[#30363D]"
            title="Zoom out"
          >
            <ZoomOut size={14} />
          </button>
          <button
            type="button"
            onClick={onZoomReset}
            className="px-2 py-1 rounded-md bg-[#21262D] hover:bg-[#30363D] text-xs font-mono min-w-[54px]"
            title="Reset zoom to 100%"
          >
            {Math.round((currentZoom || 1) * 100)}%
          </button>
          <button
            type="button"
            onClick={onZoomIn}
            className="p-1.5 rounded-md bg-[#21262D] hover:bg-[#30363D]"
            title="Zoom in"
          >
            <ZoomIn size={14} />
          </button>
        </div>
      )}
    </div>
    </div>
  );
}
